import { useI18n } from '../../i18n';
import { useGameData } from '../../state/game';
import { BottleCap } from '../app/BottleCap';
import { StatusBadge } from '../app/StatusBadge';
import { CrowdPicksBar, outcomeOf } from '../app/CrowdPicksBar';
import { shortDate, shortTime } from '../../lib/datetime';
import {
  type BundleMatch,
  isLocked,
  sideLabel,
  gradeScore,
  wentToExtraTime,
  wentToPens,
} from '../../lib/wcTypes';

/**
 * Compact match row for the Play list. Shows both sides (cap + name), the
 * kickoff or running/final score, status, and — once predicted — the user's
 * pick chip with points earned. Links to the expanded match detail.
 */
export function MatchCard({ match }: { match: BundleMatch }) {
  const { t, locale, localePath } = useI18n();
  const { bundle, pickFor, pointsFor, distributions } = useGameData();
  if (!bundle) return null;

  const pick = pickFor(match.number);
  const pts = pointsFor(match.number);
  const dist = distributions.get(match.number) ?? null;
  const nameA = sideLabel(bundle, match.team_a, match.team_a_slot, locale);
  const nameB = sideLabel(bundle, match.team_b, match.team_b_slot, locale);
  const teamA = match.team_a ? bundle.teams[match.team_a] : undefined;
  const teamB = match.team_b ? bundle.teams[match.team_b] : undefined;
  // Primary score is the 90' full-time result (what's graded); a small
  // marker flags when the match went to extra time / penalties.
  const grade = gradeScore(match);
  const koMarker = wentToPens(match)
    ? t('game.pens')
    : wentToExtraTime(match)
      ? t('game.aet')
      : null;
  const locked = isLocked(match);

  return (
    <a class="mc" href={localePath(`/match/${match.number}`)}>
      <div class="mc__top">
        <span class="mc__when">
          {shortDate(match.kickoff_utc, locale)} · {shortTime(match.kickoff_utc, locale)}
          {match.group ? ` · ${t('groups.group')} ${match.group}` : ''}
        </span>
        <StatusBadge status={match.status} minute={match.minute} />
      </div>

      <div class="mc__teams">
        <div class="mc__team">
          <BottleCap flag={teamA?.flag} code={match.team_a} size={34} title={nameA} />
          <span class="mc__name">{nameA}</span>
        </div>
        <div class="mc__score">
          {grade ? (
            <>
              <span class="bc-serif mc__scoreval">
                {grade[0]}–{grade[1]}
              </span>
              {koMarker ? <span class="mc__komark">{koMarker}</span> : null}
            </>
          ) : (
            <span class="bc-serif mc__vs">{t('game.vs')}</span>
          )}
        </div>
        <div class="mc__team mc__team--right">
          <span class="mc__name">{nameB}</span>
          <BottleCap flag={teamB?.flag} code={match.team_b} size={34} title={nameB} />
        </div>
      </div>

      <div class="mc__foot">
        {pick ? (
          <span class={`mc__pick${locked && pts > 0 ? ' is-good' : ''}`}>
            {t('game.your_pick')} {pick.scoreA}–{pick.scoreB}
            {pick.boost ? ' ×2' : ''}
            {match.status === 'done' ? ` · +${pts}` : ''}
          </span>
        ) : !locked ? (
          <span class="mc__pick mc__pick--empty">{t('game.tap_to_predict')}</span>
        ) : (
          <span class="mc__pick mc__pick--missed">{t('game.no_pick')}</span>
        )}
        <CrowdPicksBar dist={dist} myPick={pick ? outcomeOf(pick.scoreA, pick.scoreB) : undefined} />
      </div>
    </a>
  );
}
